import React, { useCallback, useEffect, useState } from 'react';
import CodeMirror, { EditorView } from '@uiw/react-codemirror';
import { html } from '@codemirror/lang-html';
import { abcdef } from '@uiw/codemirror-theme-abcdef';
import { Clipboard, ClipboardCheck } from 'react-bootstrap-icons';
import * as Styled from './EmbedCodeEditorComponent.styled';
import { useAuth } from '../../behavioral';
import { adminService, userService } from '../../services';
import { Spinner } from 'react-bootstrap';
import { generateHtmlSections } from '../../libs';

interface IEmbedCodeProps {
    clientId?: string;
}

const customTheme = EditorView.theme({
  ".cm-line": {
    backgroundColor: "transparent !important", 
  },
  ".cm-activeLine": {
    backgroundColor: "transparent !important", 
  },
  ".cm-line:first-child": {
    backgroundColor: "transparent !important",
  },
  ".cm-lineNumbers .cm-gutterElement": {
    backgroundColor: "transparent !important",  
  },
});

const CodeSection = ({
    title,
    content,
    description
}: {
    title: string;
    content: string;
    description?: string;
}) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
        navigator.clipboard.writeText(content).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Styled.SectionWrapper>
            <Styled.SectionHeader>
                <Styled.SectionTitle>{title}</Styled.SectionTitle>
                <Styled.IconButton onClick={handleCopy}>
                    {copied ? <ClipboardCheck /> : <Clipboard />}
                    {copied ? ' Copied' : ' Copy'}
                </Styled.IconButton>
            </Styled.SectionHeader>
            {description && <Styled.Description>{description}</Styled.Description>}
            <CodeMirror
                value={content}
                height="auto"
                extensions={[html(), customTheme]}
                theme={abcdef}
                editable={false}
            />
        </Styled.SectionWrapper>
    );
};

export const EmbedCodeEditorComponent: React.FC<IEmbedCodeProps> = () => {
    const authInfo = useAuth();
    const [isLoading, setIsLoading] = useState(true);
    const [sections, setSections] = useState<Record<string, string>>({});
    const [fullEmbedCode, setFullEmbedCode] = useState('');

    const initializeCode = useCallback(async () => {
        setIsLoading(true);
        try {
            const userResp = await userService.getCurrentUser();
            const packageResp = await adminService.getAllPackages();
            const clientId = authInfo?.user?.username || 'XXXXXXXX';
            const userPackageId = userResp?.subscriptionInfo?.packageId;
            const matchedPackage = packageResp?.find(pkg => pkg.id === userPackageId);
            const planName = matchedPackage?.name ?? 'Default';
            const sectionedHtml = generateHtmlSections(clientId, planName);
            setSections({
                Styles: sectionedHtml.Styles,
                HTML: sectionedHtml.HTML,
                'Script Includes': sectionedHtml['Script Includes'],
                'Script Logic': sectionedHtml['Script Logic'],
            });
            setFullEmbedCode(sectionedHtml.Full);
        } catch (error) {
            console.error('Error initializing embed code:', error);
        } finally {
            setIsLoading(false);
        }
    }, [authInfo]);

    useEffect(() => {
        initializeCode();
    }, [initializeCode]);

    const newEmbedCode = `<div id="cembooClientJoinNowButton"></div>\n\n<div id="cemboo-media-library"></div>\n\n<script\n  src="${process.env.REACT_APP_CDN_URL}/library/index.js"\n  data-client-id="${authInfo?.user?.username || 'YOUR_CLIENT_ID'}"\n  data-outlet-id="YOUR_OUTLET_ID">\n</script>`;

    return (
        <Styled.EditorWrapper>
            <Styled.DeprecationBanner>
                <Styled.DeprecationTitle>⚠ This embed code format is deprecated</Styled.DeprecationTitle>
                <Styled.DeprecationText>
                    Please use the new embed code available on each outlet in the <strong>Library Outlets</strong> page. The new format is:
                </Styled.DeprecationText>
                <Styled.DeprecationCode>{newEmbedCode}</Styled.DeprecationCode>
            </Styled.DeprecationBanner>
            {isLoading ? (
                <Styled.SpinnerWrapper>
                    <Spinner animation="border">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p>Loading Embed Code...</p>
                </Styled.SpinnerWrapper>
            ) : (
                <>
                    <Styled.EditorContent>
                        <CodeSection
                            title="Embed Code"
                            content={fullEmbedCode}
                            description=""
                        />
                    </Styled.EditorContent>

                    <Styled.HeaderBar>
                        <Styled.HeaderTitle>Embed Code Sections</Styled.HeaderTitle>
                    </Styled.HeaderBar>
                    <Styled.EditorContent>
                        <CodeSection
                            title="Styles"
                            content={sections['Styles']}
                            description="These are the stylesheets for the library."
                        />
                        <CodeSection
                            title="HTML"
                            content={sections['HTML']}
                            description="These are the HTML outlets for the library. Note: The other 3 sections should also be included in order for these outlets to function."
                        />
                        <CodeSection
                            title="Script Includes"
                            content={sections['Script Includes']}
                            description="These are the library scripts."
                        />
                        <CodeSection
                            title="Script Logic"
                            content={sections['Script Logic']}
                            description="This is the initialization code."
                        />
                    </Styled.EditorContent>
                </>
            )}
        </Styled.EditorWrapper>
    );
};
